import React, { useRef, useEffect } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import {
  Bounds,
  CameraShake,
  Center,
  OrbitControls,
  useBounds,
  useGLTF,
} from "@react-three/drei";
import * as THREE from "three";
import dat from "dat.gui"; // Voeg dat.gui toe
import { Line, LineBasicMaterial, BufferGeometry, Vector3 } from "three";
import { GaasMaterial } from "./GaasMateriaal";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { formConfig } from "../form/formConfig";
import { useSpring, a } from '@react-spring/three';



const WindowModel = () => {
  const modelRef = useRef();
  const { scene } = useGLTF("/vliegenraam2.glb"); // Zorg ervoor dat het pad correct is


  // Voorkom herhaaldelijke acties bij re-renders
  useEffect(() => {
    if (scene) {
      scene.rotateY(0.20)
    }
  }, [scene]); // Alleen uitvoeren wanneer `scene` verandert

  const boxHelperRef = useRef(); // Ref voor de bounding box helper

  console.log("rerender WindowModel");
  // Dynamisch aanpassen van de bounds bij wijzigingen in dimensies
  const bounds = useBounds();

  const default_frame_code = formConfig.find(
    (x) => x.name == "FRAME_COLOR"
  ).defaultValue;
  const default_gaas_code = formConfig.find(
    (x) => x.name == "GAAS_TYPE"
  ).defaultValue;

  const { WIDTH, HEIGHT } = useWatch({ name: "DIMENSION" });

  const frameColor = useWatch({ name: "FRAME_COLOR" }) || default_frame_code; // Voor de framekleur
  const gaas_type = useWatch({ name: "GAAS_TYPE" }) || default_gaas_code; // Voor de gaaskleur

  console.log(frameColor);
  console.log(gaas_type);

  const alphaMapTexture = useLoader(
    THREE.TextureLoader,
    "./data/gaas/Fence008A_1K-JPG_Opacity.jpg"
  );

  useEffect(() => {
    if (!scene) return;

    const option_color = formConfig
      .find((x) => x.name == "FRAME_COLOR")
      .options.find((x) => x.code == frameColor);
    const option_gaas = formConfig
      .find((x) => x.name == "GAAS_TYPE")
      .options.find((x) => x.code == gaas_type);

    console.log(option_color);
    console.log(option_gaas);

    // Stel de nieuwe kleuren in voor het frame en het gaas
    const newFrameMaterial = new THREE.MeshStandardMaterial({
      color: option_color.color, // Dynamische kleur voor het frame
      metalness: 0,
      roughness: 0.8,
      name: "Material",
    });

    // Pastel kleur (lichtblauw als voorbeeld)
    //const pastelColor = new THREE.Color(0xaec6cf);
    const pastelColor = new THREE.Color(0x383e42);

    // Maak het materiaal
    const pvcMaterial = new THREE.MeshPhysicalMaterial({
      color: option_color.color, // Dynamische kleur voor het frame
      roughness: 0.4, // Hoe ruw het oppervlak is (0 = spiegelend, 1 = mat)
      metalness: 0.2, // Plastic is niet metaalachtig, dus dit is 0
      clearcoat: 0.1, // Zorg voor een lichte plasticachtige coating
      clearcoatRoughness: 0.2, // Hoe ruw de coating is
      reflectivity: 0.5, // Reflectiviteit van het materiaal
      name: "Steel",
      transparent: true,
      opacity: 0.4,
    });

    const newDoorMaterial = new THREE.MeshStandardMaterial({
      color: "#ffffff", // Dynamische kleur voor het frame
      metalness: 0.2,
      roughness: 0.8,
      name: "Steel",
      visible: true,
      wireframe: false,
      transparent: true,
      opacity: 1,
    });

    alphaMapTexture.wrapS = alphaMapTexture.wrapT = THREE.RepeatWrapping;
    alphaMapTexture.repeat.set(7.5 * (HEIGHT / 500), 7.5 * (WIDTH / 500)); // Verhoog deze waarden voor een fijnere maas

    const newGaasMaterial = new THREE.MeshStandardMaterial({
      color: option_gaas.color, // Dynamische kleur voor het gaas
      transparent: true,
      opacity: 0.65,
      alphaMap: alphaMapTexture, // AlphaMap behouden
      metalness: 1,
      roughness: 0.8,
      name: "Glas",
    });

    // Update materialen in het 3D-model
    scene.traverse((child) => {
      if (child.isMesh) {
        console.log(child.material.name);
        switch (child.material.name) {
          case "Glas": // Gaas
            child.material = newGaasMaterial;
            break;
          case "Material": // Gaas
            child.material = newFrameMaterial;
            break;
          default: // Frame
            child.material = pvcMaterial;
            break;
        }
      }
    });
  }, [frameColor, gaas_type, alphaMapTexture, WIDTH, HEIGHT]);

  /*
  useEffect(() => {
    if (!scene || !modelRef.current) return;

    // Voeg een bounding box helper toe
    const boundingBox = new THREE.Box3().setFromObject(scene); // Bepaal de bounding box van het object
    if (boxHelperRef.current) {
      modelRef.current.remove(boxHelperRef.current); // Verwijder de oude helper
    }

    const boxHelper = new THREE.Box3Helper(boundingBox, 0xff0000); // Rood voor visualisatie
    boxHelperRef.current = boxHelper;
    modelRef.current.add(boxHelper); // Voeg de helper toe aan het model

  }, [scene, WIDTH, HEIGHT]);*/

  useEffect(() => {
    if (!HEIGHT || !WIDTH) {
      //bounds.refresh();
      return;
    }

    alphaMapTexture.wrapS = alphaMapTexture.wrapT = THREE.RepeatWrapping;
    alphaMapTexture.repeat.set(7.5 * (HEIGHT / 500), 7.5 * (WIDTH / 500)); // Verhoog deze waarden voor een fijnere maas

    // Pas morphTargetInfluences aan op basis van 'dimensions'
    scene.traverse((child) => {
      if (child.isMesh && child.morphTargetInfluences) {
        // Stel morphTargets in op basis van de dimensies

        // Mapping voor hoogte (500mm - 2500mm)
        const heightKeyshape = Math.max(
          0,
          Math.min(1, (HEIGHT - 500) / (2500 - 500))
        );

        // Mapping voor breedte (500mm - 1500mm)
        const widthKeyshape = Math.max(
          0,
          Math.min(1, (WIDTH - 500) / (1500 - 500))
        );

        if (child.morphTargetInfluences[0] !== undefined)
          child.morphTargetInfluences[0] = heightKeyshape; // Schaalwaarde voor breedte
        if (child.morphTargetInfluences[1] !== undefined)
          child.morphTargetInfluences[1] = widthKeyshape; // Schaalwaarde voor hoogte
      }
    });

    //bounds.refresh().clip().fit()
    //bounds.moveTo([0, 10, 10]).lookAt({ target: [5, 5, 0], up: [0, -1, 0] })
    //bounds.refresh(modelRef.current).fit()
  }, [WIDTH, HEIGHT]); // Trigger effect bij wijzigingen in dimensies

  return <primitive ref={modelRef} object={scene} />;
};

const Raam = () => {
  const modelRef = useRef();
  const { scene } = useGLTF("/window_2_a.glb"); // Zorg ervoor dat het pad correct is

  return <primitive ref={modelRef} object={scene} />;
};


const interpolateFunc = (t) => 1 - Math.exp(-5 * t) + 0.007 * t // Matches the default Bounds behavior
const interpolateFunc1 = (t) => -t * t * t + 2 * t * t          // Start smoothly, finish linearly
const interpolateFunc2 = (t) => -t * t * t + t * t + t          // Start linearly, finish smoothly


export const ConfiguratorContainer = () => {
  console.log("rerender 3D configurator");
  const [spring, api] = useSpring(() => ({
    color: 'white',
    config: { tension: 280, friction: 60 },
  }));

  
  return (
    <Canvas
      shadows
      style={{
        width: "100%",
        flex: 1,
        
      }}
    >
      
      {/* Basis licht */}
      <ambientLight intensity={0.5} />

      {/* PointLight met schaduw */}
      <pointLight
        position={[0, 0, 5]}
        intensity={1}
        castShadow // Zorgt ervoor dat deze lichtbron schaduw werpt
        shadow-mapSize-width={1024} // Verbeter de resolutie van de schaduw
        shadow-mapSize-height={1024}
      />

      {/* Directional light met schaduw */}
      <directionalLight
        position={[10, 10, 10]}
        intensity={1.5}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />

      {/* SpotLight met schaduw */}
      <spotLight
        position={[5, 5, 5]}
        intensity={2}
        angle={Math.PI / 4}
        penumbra={1}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />

      <OrbitControls
        minAzimuthAngle={-Math.PI / 6} // Beperk rotatie Y-as tot -30°
        maxAzimuthAngle={Math.PI / 6} // Beperk rotatie Y-as tot +30°
        minPolarAngle={Math.PI / 4} // Beperk hoogte tot minimaal 45° boven de horizon
        maxPolarAngle={Math.PI / 2} // Beperk hoogte tot maximaal 90° (recht naar beneden kijken)
        enableZoom={false} // Zoom toestaan
        zoomSpeed={0.5} // Pas zoomsnelheid aan
        enablePan={false} // Pannen uitschakelen
        makeDefault
      />

      <Bounds
        //interpolateFunc={interpolateFunc2} // Probeer hier een elastic easing of een sinusvorm.
        fit
        clip
        observe
        margin={0.8}
        maxDuration={3}
        
>
      
        
        
        <Center>
          <WindowModel />
        </Center>
      </Bounds>
    </Canvas>
  );
};
